import ActionsPage from 'ActionsPage';
import AllArticles from 'AllArticles';
import FAQAdmin from 'FAQAdmin';
import { Permissions } from 'Permission';
import { PermissionGate } from 'PermissionContext';
import React, { Suspense } from 'react';
import { Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AcceptInvitationPage } from '../../Advisor/AcceptInvitationPage';
import { RejectInvitationModal } from '../../Advisor/RejectInvitationModal';
import Forward from '../../Forward';
import { EntityManagement } from '../../InvestmentEntity/EntityManagement';
import EmailClickTracker, { EmailTrackingLayout } from '../../EmailClickTracker';
import ContactOpportunitiesPage from '../../../pages/admin/ContactOpportunitiesPage';
import ContactOverviewPage from '../../../pages/admin/ContactOverviewPage';
import NewsAdmin from '../../../pages/admin/NewsAdmin';
import ContactPortfolioPage from '../../../pages/admin/ContactPortfolioPage';
import ImpersonateAppHandoffGate from '../../../pages/admin/ImpersonateAppHandoffGate';
import ReferralInvitationAdminPage from '../../../pages/admin/ReferralInvitationAdminPage';
import SettingsPage from '../../../pages/admin/SettingsPage';
import ActivityLogPage from '../ActivityLogPage';
import { AdminEntityAssociationWizard } from '../AdminEntityAssociationWizard';
import AdminUserPermissions from '../AdminUserPermissions';
import AIChatFundOpportunityCreator from '../AIChatFundOpportunityCreator';
import ArticlesForApproval from '../ArticlesForApproval';
import EmailPreviewIframe from '../EmailPreviewIframe';
import GlobalConfigAdmin from '../GlobalConfigAdmin';
import JobsPage from '../JobsPage';
import ModelActivityLogPage from '../ModelActivityLogPage';
import PropertiesActiveTabs from '../PropertiesActiveTabs';
import PropertiesPending from '../PropertiesPending';
import RecentlyViewedPage from 'RecentlyViewedPage';
import ReviewRequestsPage from '../ReviewRequestsPage';
import ReviewViewer from '../ReviewViewer';
import VeraGlobalChatPromptOverrideAdmin from '../VeraGlobalChatPromptOverrideAdmin';
import {
  CheckLoginComponentWithKey,
  CheckLoginComponentWithKeyAndLayout,
} from './CheckLoginComponent';
import {
  CreateAdminUserWrapper,
  AdminUserAdminListRoute,
  AdminUserAdminWithId,
  ContactAdminListRoute,
  ContactAdminWithId,
  ContentRouteWrapper,
  ContentVersionViewerWrapper,
  GenerateInvitationEmailWrapper,
  LoginAndArticle,
  LoginAndFundOpportunity,
  OpportunityAdminWrapper,
  OpportunityAIWizardWrapper,
  OpportunityFundUpdateWrapper,
  OpportunityInvestorWrapper,
  PlaceholderContactAdminWrapper,
} from './adminAppWrappers';
import { HandleForwardURL, Logout, ResetPasswordCodeHandler } from './adminAppAuth';
import LoadingSpinner from './LoadingSpinner';
import LookupOpportunity from './LookupOpportunity';

export interface AdminAppRoutesProps {
  createOpportunity: () => void;
  setOppNavigate: (url: string) => void;
}

export default function AdminAppRoutes({ createOpportunity, setOppNavigate }: AdminAppRoutesProps) {
  const location = useLocation();

  return (
    <>
      <EmailClickTracker />
      <Routes>
        <Route element={<EmailTrackingLayout isAdmin={true} />}>
          <Route path="/login" element={<HandleForwardURL Component="Login" />} />
          <Route path="/resetPassword" element={<ResetPasswordCodeHandler />} />
          <Route path="/adminSignup/:invitationToken" element={<CreateAdminUserWrapper />} />
          <Route path="/logout" element={<Logout />} />

          <Route
            path="/accept-invitation/:invitationId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <AcceptInvitationPage isAdmin={true} />
              </Suspense>
            }
          />
          <Route
            path="/reject-invitation/:invitationId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <RejectInvitationModal isAdmin={true} />
              </Suspense>
            }
          />

          <Route path="/email-preview-iframe" element={<EmailPreviewIframe />} />

          {/*
           * The app-impersonation handoff landing page must render for any signed-in
           * user, not just admins: the admin + end-user apps share one Auth0 tenant,
           * so a phone that scans the QR may be signed in as an end user. It uses its
           * own auth gate (not CheckLoginComponent / the admin layout, both of which
           * blank the screen for non-admins or on Android Chrome's flaky silent-auth)
           * and shows its own "not an admin" message.
           */}
          <Route path="/impersonate/app" element={<ImpersonateAppHandoffGate />} />

          <Route
            element={
              <CheckLoginComponentWithKeyAndLayout>
                <Outlet />
              </CheckLoginComponentWithKeyAndLayout>
            }
          >
            <Route
              path="/"
              element={<PropertiesActiveTabs onAddClicked={() => createOpportunity()} />}
            />
            <Route path="/forward" element={<Forward />} />
            <Route
              path="/opportunities/activePending"
              element={<PropertiesPending onAddClicked={() => createOpportunity()} />}
            />
            <Route
              path="/opportunities/:id/content"
              element={
                <CheckLoginComponentWithKey>
                  <ContentRouteWrapper />
                </CheckLoginComponentWithKey>
              }
            />
            <Route
              path="/opportunities/:id/print/draft"
              element={
                <CheckLoginComponentWithKey>
                  <LookupOpportunity type="FundOpportunity" printDraft={true} print={true} />
                </CheckLoginComponentWithKey>
              }
            />
            <Route
              path="/opportunities/:id/print"
              element={
                <CheckLoginComponentWithKey>
                  <LookupOpportunity type="FundOpportunity" print={true} />
                </CheckLoginComponentWithKey>
              }
            />
            <Route
              path="/opportunities/:id/investors/:investorId"
              element={<OpportunityInvestorWrapper />}
            />
            <Route
              path="/opportunities/:id/fundUpdate"
              element={<OpportunityFundUpdateWrapper />}
            />
            <Route
              path="/opportunities/:id/irrManagement"
              element={<LoginAndFundOpportunity type="IRRManagement" />}
            />
            <Route
              path="/opportunities/new"
              element={
                <CheckLoginComponentWithKey>
                  <AIChatFundOpportunityCreator
                    onSuccess={(url) => {
                      setOppNavigate(url);
                    }}
                  />
                </CheckLoginComponentWithKey>
              }
            />
            <Route
              path="/opportunities/:id/generateContent"
              element={<OpportunityAIWizardWrapper />}
            />
            <Route path="/opportunities/:id" element={<OpportunityAdminWrapper />} />
            <Route path="/articles/:id" element={<LoginAndArticle type="ArticleApproval" />} />
            <Route path="/articlesForApproval" element={<ArticlesForApproval />} />
            <Route path="/allArticles" element={<AllArticles />} />
            <Route
              path="/news"
              element={
                <PermissionGate permissionKey={Permissions.NewsArticleCreation}>
                  <NewsAdmin />
                </PermissionGate>
              }
            />
            <Route path="/contacts" element={<ContactAdminListRoute />} />
            <Route path="/contacts/:id/overview" element={<ContactOverviewPage />} />
            <Route path="/contacts/:id/portfolio" element={<ContactPortfolioPage />} />
            <Route path="/contacts/:id/opportunities" element={<ContactOpportunitiesPage />} />
            <Route path="/contacts/:id" element={<ContactAdminWithId key={location.pathname} />} />
            <Route
              path="/contacts/:id/generateInvitationEmail"
              element={<GenerateInvitationEmailWrapper />}
            />
            <Route path="/email-preview-iframe" element={<EmailPreviewIframe />} />
            <Route path="/adminUsers" element={<AdminUserAdminListRoute />} />
            <Route
              path="/adminUsers/:id"
              element={<AdminUserAdminWithId key={location.pathname} />}
            />
            <Route
              path="/adminUsers/:adminUserId/permissions"
              element={
                <PermissionGate permissionKey={Permissions.AdminUserCreation}>
                  <AdminUserPermissions />
                </PermissionGate>
              }
            />
            <Route
              path="/globalConfig"
              element={
                <PermissionGate permissionKey={Permissions.EditGlobalConfiguration}>
                  <GlobalConfigAdmin />
                </PermissionGate>
              }
            />
            <Route
              path="/faqAdmin"
              element={
                <PermissionGate permissionKey={Permissions.ContentCreation}>
                  <FAQAdmin />
                </PermissionGate>
              }
            />
            <Route
              path="/veraGlobalChatPromptOverride"
              element={
                <PermissionGate permissionKey={Permissions.AIDeveloper}>
                  <VeraGlobalChatPromptOverrideAdmin />
                </PermissionGate>
              }
            />
            <Route
              path="/vera/prompt-proposals/:proposalId"
              element={
                <PermissionGate permissionKey={Permissions.AIDeveloper}>
                  <VeraGlobalChatPromptOverrideAdmin />
                </PermissionGate>
              }
            />
            <Route
              path="/vera/preview-chat/:proposalId"
              element={
                <PermissionGate permissionKey={Permissions.AIDeveloper}>
                  <VeraGlobalChatPromptOverrideAdmin />
                </PermissionGate>
              }
            />
            <Route
              path="/associate-entity/:investorId/:selectedEntityId/:currentState"
              element={<AdminEntityAssociationWizard />}
            />
            <Route
              path="/associate-entity/:investorId/:currentState"
              element={<AdminEntityAssociationWizard />}
            />
            <Route
              path="/associate-entity/:investorId"
              element={<AdminEntityAssociationWizard />}
            />
            <Route path="/entities/:entityId" element={<EntityManagement />} />
            <Route path="/investment-entities/:entityId" element={<EntityManagement />} />
            <Route path="/actions" element={<ActionsPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/reviews" element={<ReviewRequestsPage tone="tone1" />} />
            <Route path="/review/:reviewId" element={<ReviewViewer tone="tone1" />} />
            <Route path="/activityLogs" element={<ActivityLogPage tone="tone1" />} />
            <Route
              path="/activityLogs/:modelType/:modelId"
              element={<ModelActivityLogPage tone="tone1" />}
            />
            <Route
              path="/content-version/:activityLogId"
              element={<ContentVersionViewerWrapper tone="tone1" />}
            />
            <Route path="/recentlyViewed" element={<RecentlyViewedPage tone="tone1" />} />
            <Route
              path="/placeholderContact/:placeholderId"
              element={<PlaceholderContactAdminWrapper />}
            />
            <Route
              path="/settings"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <SettingsPage />
                </Suspense>
              }
            />
            <Route
              path="/referral-invitations/:id"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <ReferralInvitationAdminPage />
                </Suspense>
              }
            />
          </Route>
        </Route>
      </Routes>
    </>
  );
}
